import { Avatar } from "@moe/oss/ui/avatar";
import { cn } from "@moe/priv/utils";
import { CardActionBar } from "@web/components/CardActionBar";
import { CreatorLink } from "@web/components/CreatorLink";
import { TagChip } from "@web/components/tag/TagChip";
import { useAppContext } from "@web/route-services/root/AppContext";
import { motion } from "motion/react";

interface Props {
  title: string;
  tagline: string;
  avatar?: string;
  createdBy?: string;
  externalCreatedBy?: string;
  likeCount: number;
  isLiked: boolean;
  tags?: string[];
  onTagSelect?: (tag: string) => void;
  messageCount: number;
  className?: string;
  onLike?: () => void;
  onChat?: () => void;
  disableCreatorLink?: boolean;
  onClick?: () => void;
  onShare?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
}

/**
 * A pure UI (no behavior) component for displaying information about a character.
 */
export function VerticalCardView({
  title,
  tagline,
  avatar,
  createdBy,
  externalCreatedBy,
  likeCount,
  isLiked,
  messageCount,
  disableCreatorLink,
  tags,
  onTagSelect,
  onLike,
  onChat,
  onShare,
  onClick,
  onMouseDown,
  className,
  ...rest
}: Props) {
  const { isDesktopScreen } = useAppContext();
  const tagsLimit = isDesktopScreen ? 10 : 0;
  const limitedTags = tags?.slice(0, tagsLimit);

  return (
    <div
      className={cn(
        `rounded-4xl bg-moe-card border-line hover:bg-moe-card-hover relative flex h-full w-full cursor-pointer touch-manipulation flex-col items-center overflow-hidden border duration-300 ease-out focus:outline-none md:w-[232px]`,
        className
      )}
      onClick={onClick}
      onMouseDown={onMouseDown}
      {...rest}
    >
      <Avatar className={cn("h-[183px] w-full select-none rounded-none md:h-[205px]")} src={avatar} />
      <CardActionBar
        like={{
          likeCount,
          isLiked,
          onLike
        }}
        chat={{
          messageCount,
          onChat
        }}
        share={{
          onShare
        }}
      />
      <div className={"flex h-full w-full grow flex-col pb-4 pl-3.5 pr-2 pt-2 md:px-4 md:pb-5"}>
        <div className="flex grow flex-col">
          <div className="text-tx-primary line-clamp-1 w-full truncate text-base font-bold">{title}</div>
          <CreatorLink disabled={!!disableCreatorLink} createdBy={createdBy} externalCreatedBy={externalCreatedBy} />

          <p className="text-tx-secondary mb-2 line-clamp-6 whitespace-pre-wrap text-sm font-medium md:text-sm">
            {tagline}
          </p>

          <div className="mt-2 flex flex-wrap gap-1">
            {limitedTags?.map((tag, index) => <TagChip key={index} tag={tag} onSelect={onTagSelect} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
